import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { generateReviewQr, generateWalkinQr, uploadCustomerPhoto } from '@services/liveReviewService';
import type { MaskResult } from '@utils/faceMask';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import BrushIcon from '@mui/icons-material/Brush';
import ImageIcon from '@mui/icons-material/Image';
import ManualBlurEditor from './ManualBlurEditor';

type PhotoMode = 'original' | 'manual';

// Selfie captures come from the front camera (mirrored by sensor);
// gallery picks are already authored. detectMirror() picks the right
// preview behaviour so the user sees what they expect to see.
const detectMirror = (fileName: string): boolean => fileName.startsWith('capture-');

interface QuickReviewReservation {
  _id: string;
  customerName?: string;
}

interface QuickReviewStylistOption {
  _id: string;
  displayName?: string;
  email?: string;
}

interface QuickReviewModalProps {
  open: boolean;
  onClose: () => void;
  reservation: QuickReviewReservation | null;
  // Walk-in mode: no reservation, creates a verified single-use walk-in visit.
  walkin?: boolean;
  // List of stylists the caller can pick from for walk-in / poster QRs.
  // When provided AND length > 1, a stylist dropdown is rendered (business role).
  stylists?: QuickReviewStylistOption[];
}

const QuickReviewModal = ({ open, onClose, reservation, walkin = false, stylists }: QuickReviewModalProps) => {
  const { t } = useTranslation();
  const showStylistPicker = !!(walkin && stylists && stylists.length > 1);
  const [selectedStylistId, setSelectedStylistId] = useState<string>('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [rawPhotoFile, setRawPhotoFile] = useState<File | null>(null); // the original capture (un-masked), paired with photoFile for the toggle
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [masking, setMasking] = useState<boolean>(false);
  const [photoMasked, setPhotoMasked] = useState<boolean>(false);
  const [blurEditorOpen, setBlurEditorOpen] = useState<boolean>(false);
  const [manualBlurred, setManualBlurred] = useState<boolean>(false);
  // Default 'original': don't auto-mask unless the stylist asks. The picker
  // sits ABOVE the capture button so the mode is chosen before the photo.
  const [photoMode, setPhotoMode] = useState<PhotoMode>('original');
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false);
  const [photoUploaded, setPhotoUploaded] = useState<boolean>(false);
  const [generatingQr, setGeneratingQr] = useState<boolean>(false);
  const [reviewUrl, setReviewUrl] = useState<string>('');
  const qrRef = useRef<HTMLDivElement>(null);

  // Reset all state whenever the modal is opened for a (potentially new) reservation.
  useEffect(() => {
    if (open) {
      setPhotoFile(null);
      setRawPhotoFile(null);
      setPhotoPreview(null);
      setMasking(false);
      setPhotoMasked(false);
      setBlurEditorOpen(false);
      setManualBlurred(false);
      setPhotoMode('original');
      setUploadingPhoto(false);
      setPhotoUploaded(false);
      setGeneratingQr(false);
      setReviewUrl('');
      // Default the stylist picker to the first option when it appears.
      if (showStylistPicker && stylists && stylists.length > 0) {
        setSelectedStylistId(stylists[0]._id);
      }
    }
  }, [open, reservation?._id, showStylistPicker, stylists]);

  // Revoke the object URL when the preview changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handlePhotoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setRawPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoUploaded(false);
    setManualBlurred(false);
    // Apply the current mode to the freshly-picked file.
    if (photoMode === 'original') {
      setPhotoFile(file);
    } else {
      // 'manual' — open the editor right away
      setPhotoFile(file);
      setBlurEditorOpen(true);
    }
  };

  // Mode-toggle handler. Re-runs the conversion on the current rawPhotoFile
  // (if any) so the stylist can flip between modes without retaking.
  const handleModeChange = async (next: PhotoMode) => {
    setPhotoMode(next);
    if (!rawPhotoFile) return;
    setPhotoUploaded(false);
    if (next === 'original') {
      setPhotoFile(rawPhotoFile);
    } else {
      // manual
      setPhotoFile(rawPhotoFile);
      setManualBlurred(false);
      setBlurEditorOpen(true);
    }
  };

  // Manual finger-blur editor: swap the raw photo for the masked File the
  // editor produced. The appointment flow must re-upload (photoUploaded=false);
  // the walk-in flow attaches whatever photoFile holds when the QR is made.
  const handleBlurConfirm = (result: MaskResult) => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(result.file);
    setPhotoPreview(result.previewUrl);
    setPhotoUploaded(false);
    setManualBlurred(true);
    setBlurEditorOpen(false);
    setPhotoMode('manual');
    toast.success(t('liveReview.photo.manualBlurred'));
  };

  const handleUploadPhoto = async () => {
    if (!reservation || !photoFile) return;
    setUploadingPhoto(true);
    try {
      // Pass the raw sibling whenever the mode isn't 'original' so the
      // consumer can offer the "blur salon original" toggle.
      const original = photoMode !== 'original' ? rawPhotoFile ?? undefined : undefined;
      await uploadCustomerPhoto(
        { reservationId: reservation._id, photoMode },
        photoFile,
        original,
      );
      setPhotoUploaded(true);
      toast.success(t('liveReview.photoUploaded'));
    } catch (err) {
      console.error('[QuickReviewModal] appointment photo upload failed:', err);
      const ax = err as { response?: { data?: { message?: string } } };
      toast.error(`${t('liveReview.photoUploadError')}${ax?.response?.data?.message ? ` (${ax.response.data.message})` : ''}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleGenerateQr = async () => {
    setGeneratingQr(true);
    try {
      if (walkin) {
        // Create the walk-in visit first, then (optionally) attach the photo to it.
        // For business owners with multiple stylists, pass the selected stylist so the
        // single-use QR is attributed to them server-side.
        const { reviewUrl: url, walkinJti } = await generateWalkinQr(selectedStylistId || undefined);
        if (photoFile && walkinJti) {
          try {
            const original = photoMode !== 'original' ? rawPhotoFile ?? undefined : undefined;
            await uploadCustomerPhoto({ walkinJti, photoMode }, photoFile, original);
            setPhotoUploaded(true);
          } catch (err) {
            console.error('[QuickReviewModal] walk-in photo upload failed:', err);
            const ax = err as { response?: { data?: { message?: string } } };
            toast.error(`${t('liveReview.photoUploadError')}${ax?.response?.data?.message ? ` (${ax.response.data.message})` : ''}`);
          }
        }
        setReviewUrl(url);
      } else {
        if (!reservation) return;
        const { reviewUrl: url } = await generateReviewQr(reservation._id);
        setReviewUrl(url);
      }
    } catch {
      toast.error(t('liveReview.qrError'));
    } finally {
      setGeneratingQr(false);
    }
  };

  const handleDownloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'review-qr.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('liveReview.title')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {!walkin && reservation?.customerName && (
            <Typography variant="body2" color="text.secondary">
              {t('liveReview.forCustomer', { name: reservation.customerName })}
            </Typography>
          )}

          {walkin && (
            <Typography variant="body2" color="text.secondary">
              {t('liveReview.walkinIntro')}
            </Typography>
          )}

          {/* Stylist picker for business owners with multiple stylists. */}
          {showStylistPicker && stylists && (
            <FormControl fullWidth size="small">
              <InputLabel id="quick-review-stylist-label">{t('liveReview.photo.stylistLabel')}</InputLabel>
              <Select
                labelId="quick-review-stylist-label"
                label={t('liveReview.photo.stylistLabel')}
                value={selectedStylistId || stylists[0]?._id || ''}
                onChange={(event) => setSelectedStylistId(event.target.value as string)}
                disabled={!!reviewUrl}
              >
                {stylists.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    {s.displayName || s.email || s._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Step 1: optional customer photo */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('liveReview.photoStep')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('liveReview.photoHint')}
            </Typography>
            {photoPreview && (
              <Box
                component="img"
                src={photoPreview}
                alt={t('liveReview.photoStep')}
                // Selfie cameras (front-facing) capture mirrored by default — the
                // user expects the preview to look like a mirror, not the raw
                // sensor output. Gallery imports are not mirrored (already
                // authored by the user). detectMirror() flags the latter.
                sx={detectMirror(rawPhotoFile?.name ?? '')
                  ? { width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1, mb: 1, transform: 'scaleX(-1)' }
                  : { width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 1, mb: 1 }
                }
              />
            )}
            {/* Mode picker — shown AFTER capture so the stylist picks how the
                photo is processed, after seeing the result. Two options only.
                Default: Original (no mask). */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('liveReview.photo.mode')}
              </Typography>
              {/* Mode picker shows AFTER capture so the stylist picks how to
                  process this specific photo. Two options only — auto-mask was
                  removed (per UX feedback). Default: original. */}
              {photoFile && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  fullWidth
                  value={photoMode}
                  onChange={(_e, v) => v && handleModeChange(v as PhotoMode)}
                  aria-label={t('liveReview.photo.mode')}
                  disabled={!!reviewUrl}
                >
                  <ToggleButton value="original">
                    <Tooltip title={t('liveReview.photo.modeOriginalHint')}><ImageIcon fontSize="small" sx={{ mr: 0.5 }} /></Tooltip>
                    {t('liveReview.photo.modeOriginal')}
                  </ToggleButton>
                  <ToggleButton value="manual">
                    <Tooltip title={t('liveReview.photo.modeManualHint')}><BrushIcon fontSize="small" sx={{ mr: 0.5 }} /></Tooltip>
                    {t('liveReview.photo.modeManual')}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" component="label" size="small" disabled={!!reviewUrl}>
                {photoFile ? t('liveReview.changePhoto') : t('liveReview.selectPhoto')}
                <input type="file" hidden accept="image/*" capture="environment" onChange={handlePhotoSelect} />
              </Button>
              {/* Appointment: upload immediately. Walk-in: attaches when the QR is generated. */}
              {!walkin && photoFile && !photoUploaded && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUploadPhoto}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? <CircularProgress size={20} /> : t('liveReview.uploadPhoto')}
                </Button>
              )}
              {photoUploaded && (
                <Typography variant="body2" color="success.main" sx={{ alignSelf: 'center' }}>
                  {t('liveReview.photoDone')}
                </Typography>
              )}
              {walkin && photoFile && !photoUploaded && (
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {t('liveReview.walkinPhotoHint')}
                </Typography>
              )}
            </Stack>
          </Box>

          {/* Step 2: QR */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('liveReview.qrStep')}
            </Typography>
            {reviewUrl ? (
              <>
                <Box ref={qrRef} sx={{ display: 'inline-block', p: 2, bgcolor: '#fff' }}>
                  <QRCodeCanvas value={reviewUrl} size={256} />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('liveReview.scanInstruction')}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadQr}
                  sx={{ mt: 1 }}
                >
                  {t('liveReview.download')}
                </Button>
                <Typography
                  variant="body2"
                  component='a'
                  href={reviewUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    mt: 1.5,
                    display: 'inline-block',
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('liveReview.qr.openInNewTab')}
                </Typography>
              </>
            ) : (
              <Button variant="contained" onClick={handleGenerateQr} disabled={generatingQr}>
                {generatingQr ? <CircularProgress size={24} /> : t('liveReview.generateQr')}
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
      <ManualBlurEditor
        open={blurEditorOpen}
        imageSrc={photoPreview}
        onCancel={() => setBlurEditorOpen(false)}
        onConfirm={handleBlurConfirm}
      />
    </Dialog>
  );
};

export default QuickReviewModal;
